import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { searchCommerces } from "../../../services/commercesServices";

import {
  ensureArray,
  getDistanceKm,
  normalizeCommerce,
  normalizeSearchText,
} from "../utils/commercesUtils";

const INITIAL_FILTERS = {
  q: "",
  categorie: "",
  ville: "",
  hasPhoto: false,
  useGeo: false,
  sort: "title",
  dir: "ASC",
};

function extractItems(response) {
  if (Array.isArray(response)) return response;
  return ensureArray(response?.items);
}

function sortCommerces(items, filters) {
  const direction = filters.dir === "DESC" ? -1 : 1;

  return [...items].sort((a, b) => {
    if (filters.sort === "distance") {
      const da = Number.isFinite(Number(a.distanceKm))
        ? Number(a.distanceKm)
        : Number.POSITIVE_INFINITY;
      const db = Number.isFinite(Number(b.distanceKm))
        ? Number(b.distanceKm)
        : Number.POSITIVE_INFINITY;

      return (da - db) * direction;
    }

    if (filters.sort === "category") {
      return (
        normalizeSearchText(a.category).localeCompare(
          normalizeSearchText(b.category),
          "fr"
        ) * direction
      );
    }

    if (filters.sort === "city") {
      return (
        normalizeSearchText(a.ville).localeCompare(
          normalizeSearchText(b.ville),
          "fr"
        ) * direction
      );
    }

    return (
      normalizeSearchText(a.title).localeCompare(
        normalizeSearchText(b.title),
        "fr"
      ) * direction
    );
  });
}

export function useCommercesPage() {
  const bootedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rawItems, setRawItems] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [expandedMapId, setExpandedMapId] = useState(null);
  const [geo, setGeo] = useState({
    lat: null,
    lng: null,
    loading: false,
    error: "",
  });

  const deferredSearch = useDeferredValue(filters.q);

  const loadCommerces = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await searchCommerces({
        is_active: true,
        limit: 500,
        page: 1,
      });

      setRawItems(extractItems(response));
    } catch (err) {
      console.error("Erreur chargement commerces:", err);
      setRawItems([]);
      setError("LOAD_ERROR");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    loadCommerces();
  }, [loadCommerces]);

  const askGeolocation = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setGeo((previous) => ({
        ...previous,
        error: "La géolocalisation n'est pas supportée par votre navigateur.",
      }));
      return;
    }

    setGeo((previous) => ({
      ...previous,
      loading: true,
      error: "",
    }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGeo({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          loading: false,
          error: "",
        });
      },
      (err) => {
        console.error("Geolocation error:", err);

        setGeo({
          lat: null,
          lng: null,
          loading: false,
          error: "Impossible d'obtenir votre position.",
        });

        setFilters((previous) => ({
          ...previous,
          useGeo: false,
          sort: previous.sort === "distance" ? "title" : previous.sort,
        }));
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 0,
      }
    );
  }, []);

  useEffect(() => {
    if (filters.useGeo && (geo.lat === null || geo.lng === null)) {
      askGeolocation();
    }
  }, [askGeolocation, filters.useGeo, geo.lat, geo.lng]);

  const allItems = useMemo(() => {
    return rawItems.map((item) =>
      normalizeCommerce(item, {
        userLat: geo.lat,
        userLng: geo.lng,
      })
    );
  }, [geo.lat, geo.lng, rawItems]);

  const categories = useMemo(() => {
    return Array.from(
      new Set(
        allItems
          .map((item) => item.category)
          .filter(Boolean)
          .map((category) => String(category).trim())
      )
    ).sort((a, b) => a.localeCompare(b, "fr"));
  }, [allItems]);

  const villes = useMemo(() => {
    return Array.from(
      new Set(
        allItems
          .map((item) => item.ville)
          .filter(Boolean)
          .map((ville) => String(ville).trim())
      )
    ).sort((a, b) => a.localeCompare(b, "fr"));
  }, [allItems]);

  const filteredItems = useMemo(() => {
    const q = normalizeSearchText(deferredSearch);

    const filtered = allItems.filter((item) => {
      const matchSearch = q ? item.searchIndex.includes(q) : true;

      const matchCategory = filters.categorie
        ? item.category === filters.categorie
        : true;

      const matchVille = filters.ville ? item.ville === filters.ville : true;

      const matchPhoto = filters.hasPhoto ? Boolean(item.photo) : true;

      return matchSearch && matchCategory && matchVille && matchPhoto;
    });

    return sortCommerces(
      filtered.map((item) => {
        if (!filters.useGeo || geo.lat === null || geo.lng === null) {
          return item;
        }

        return {
          ...item,
          distanceKm: getDistanceKm(geo.lat, geo.lng, item.lat, item.lng),
        };
      }),
      filters.useGeo
        ? {
            ...filters,
            sort: "distance",
            dir: "ASC",
          }
        : filters
    );
  }, [allItems, deferredSearch, filters, geo.lat, geo.lng]);

  const updateFilter = useCallback((name, value) => {
    setExpandedMapId(null);

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  }, []);

  const toggleCategory = useCallback((category) => {
    setExpandedMapId(null);

    setFilters((previous) => ({
      ...previous,
      categorie: previous.categorie === category ? "" : category,
    }));
  }, []);

  const toggleGeo = useCallback(() => {
    setExpandedMapId(null);

    setFilters((previous) => ({
      ...previous,
      useGeo: !previous.useGeo,
      sort: !previous.useGeo ? "distance" : "title",
      dir: "ASC",
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setExpandedMapId(null);
    setFilters(INITIAL_FILTERS);
    setGeo({
      lat: null,
      lng: null,
      loading: false,
      error: "",
    });
  }, []);

  const toggleMap = useCallback((commerceId) => {
    setExpandedMapId((current) => (current === commerceId ? null : commerceId));
  }, []);

  const refresh = useCallback(() => {
    setExpandedMapId(null);
    loadCommerces();
  }, [loadCommerces]);

  return {
    loading,
    error,
    items: filteredItems,
    total: filteredItems.length,
    allTotal: allItems.length,
    categories,
    villes,
    filters,
    geo,
    expandedMapId,
    updateFilter,
    toggleCategory,
    toggleGeo,
    resetFilters,
    toggleMap,
    refresh,
  };
}